import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './context/AuthContext';
import useOffline from './hooks/useOffline';
import OfflineBanner from './components/shared/OfflineBanner';

// Auth Pages
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import WelcomePage from './pages/WelcomePage';
import SignInPage from './pages/SignInPage';
import SignUpPage from './pages/SignUpPage';
import VerifyOTPPage from './pages/VerifyOTPPage';
import CreatePinPage from './pages/CreatePinPage';
import CreateTagPage from './pages/CreateTagPage';
import SignUpSuccessPage from './pages/SignUpSuccessPage';
import IdentityVerificationPage from './pages/IdentityVerificationPage';
import PinPage from './pages/PinPage';

// App Pages
import HomePage from './pages/HomePage';
import ShopPage from './pages/ShopPage';
import StoresPage from './pages/StoresPage';
import GroupsPage from './pages/GroupsPage';
import GroupChatPage from './pages/GroupChatPage';
import DashboardPage from './pages/DashboardPage';
import MenuPage from './pages/MenuPage';
import ReferralsPage from './pages/ReferralsPage';
import ProfilePage from './pages/ProfilePage';
import TransactionReceiptPage from './pages/TransactionReceiptPage';
import StorefrontPage from './pages/StorefrontPage';
import BonndleBillsPage from './pages/BonndleBillsPage';
import GifftinsStorePage from './pages/GifftinsStorePage';
import MarketplacePage from './pages/MarketplacePage';
import ProductDetailPage from './pages/ProductDetailPage';

// Protected routes list for validation
const PROTECTED_ROUTES = ['/home', '/shop', '/stores', '/groups', '/dashboard', '/menu', '/referrals', '/profile', '/marketplace'];

// Protected Route wrapper
function ProtectedRoute({ children }) {
  const { isLoggedIn, isGuestMode } = useAuth();

  // Allow access if logged in OR in guest mode
  if (!isLoggedIn && !isGuestMode) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// Public Route wrapper (redirects to saved route or /home if already logged in)
function PublicRoute({ children }) {
  const { isLoggedIn } = useAuth();

  if (isLoggedIn) {
    // Check for saved route
    const savedRoute = localStorage.getItem('bonndle:lastRoute');
    const targetRoute = savedRoute && PROTECTED_ROUTES.includes(savedRoute) ? savedRoute : '/home';
    return <Navigate to={targetRoute} replace />;
  }

  return children;
}

export default function App() {
  const isOffline = useOffline();
  const { isLoggedIn } = useAuth();
  const location = useLocation();

  // Scroll to top on route change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  // Persist current route to localStorage when navigating (only for protected routes)
  useEffect(() => {
    if (isLoggedIn && PROTECTED_ROUTES.includes(location.pathname)) {
      localStorage.setItem('bonndle:lastRoute', location.pathname);
    }
  }, [location.pathname, isLoggedIn]);

  return (
    <div className={`min-h-screen font-sans antialiased ${isLoggedIn ? 'bg-gray-50' : 'bg-white'}`}>
      {isOffline && <OfflineBanner />}

      <Routes location={location}>
        {/* Public Routes (Auth) */}
        <Route path="/" element={<PublicRoute><LandingPage /></PublicRoute>} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/welcome" element={<PublicRoute><WelcomePage /></PublicRoute>} />
        <Route path="/signin" element={<PublicRoute><SignInPage /></PublicRoute>} />
        <Route path="/signup" element={<PublicRoute><SignUpPage /></PublicRoute>} />
        <Route path="/verify-otp" element={<PublicRoute><VerifyOTPPage /></PublicRoute>} />
        <Route path="/create-pin" element={<PublicRoute><CreatePinPage /></PublicRoute>} />
        <Route path="/create-tag" element={<PublicRoute><CreateTagPage /></PublicRoute>} />
        <Route path="/signup-success" element={<SignUpSuccessPage />} />
        <Route path="/verify-identity" element={<IdentityVerificationPage />} />
        <Route path="/pin" element={<PublicRoute><PinPage /></PublicRoute>} />

        {/* Protected Routes (App) */}
        <Route path="/home" element={<ProtectedRoute><HomePage /></ProtectedRoute>} />
        <Route path="/shop" element={<ProtectedRoute><ShopPage /></ProtectedRoute>} />
        <Route path="/stores" element={<ProtectedRoute><StoresPage /></ProtectedRoute>} />
        <Route path="/groups" element={<ProtectedRoute><GroupsPage /></ProtectedRoute>} />
        <Route path="/groups/:groupId" element={<ProtectedRoute><GroupChatPage /></ProtectedRoute>} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/menu" element={<ProtectedRoute><MenuPage /></ProtectedRoute>} />
        <Route path="/referrals" element={<ProtectedRoute><ReferralsPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/transaction/:id" element={<ProtectedRoute><TransactionReceiptPage /></ProtectedRoute>} />
        <Route path="/marketplace" element={<ProtectedRoute><MarketplacePage /></ProtectedRoute>} />
        <Route path="/marketplace/:productId" element={<ProtectedRoute><ProductDetailPage /></ProtectedRoute>} />

        {/* Storefront Routes - Public for testing */}
        <Route path="/store/bill-lion" element={<BonndleBillsPage />} />
        <Route path="/store/gifftins" element={<GifftinsStorePage />} />
        <Route path="/store/:merchantId" element={<StorefrontPage />} />

        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
